import { useState, useCallback } from 'react'

const getEffectiveDimensions = (item, reverseFlag) => {
  if (reverseFlag === undefined) reverseFlag = item.reverse_flag || false
  return {
    width: reverseFlag ? item.rightWidth || item.width || 1 : item.width || 1,
    height: reverseFlag ? item.rightHeight || item.height || 1 : item.height || 1
  }
}

export const useDragAndDrop = (gridSize, wallRows) => {
  const [droppedItems, setDroppedItems] = useState({})
  const [savedSnapshot, setSavedSnapshot] = useState({})
  const [itemAttachedAmounts, setItemAttachedAmounts] = useState({})
  const [hoverPosition, setHoverPosition] = useState(null)
  const [draggingItem, setDraggingItem] = useState(null)
  const [justDroppedKey, setJustDroppedKey] = useState(null)
  const [itemsToDestroy, setItemsToDestroy] = useState([])
  const [ghostItem, setGhostItem] = useState(null)

  const canPlace = useCallback(
    (plane, startX, startY, itemWidth, itemHeight, excludeItem) => {
      const width = itemWidth || 1
      const height = itemHeight || 1

      if (startX < 0 || startY < 0) return false

      if (
        startX + width > gridSize ||
        startY + height > (plane === 'floor' ? gridSize : wallRows)
      ) {
        return false
      }

      const excludeKeys = new Set()
      if (excludeItem) {
        const exPlane = excludeItem.plane || excludeItem.individual_space || plane
        const exDims = getEffectiveDimensions(excludeItem, excludeItem.reverse_flag)
        const exW = excludeItem.itemWidth || exDims.width
        const exH = excludeItem.itemHeight || exDims.height
        for (let dx = 0; dx < exW; dx++) {
          for (let dy = 0; dy < exH; dy++) {
            excludeKeys.add(`${exPlane}-${excludeItem.startX + dx}-${excludeItem.startY + dy}`)
          }
        }
      }

      for (let dx = 0; dx < width; dx++) {
        for (let dy = 0; dy < height; dy++) {
          const key = `${plane}-${startX + dx}-${startY + dy}`
          if (droppedItems[key] && !excludeKeys.has(key)) {
            return false
          }
        }
      }
      return true
    },
    [droppedItems, gridSize, wallRows]
  )

  const getItemAtPosition = useCallback(
    (plane, x, y) => {
      const key = `${plane}-${x}-${y}`
      return droppedItems[key] || null
    },
    [droppedItems]
  )

  const isTileHovered = useCallback(
    (plane, x, y) => {
      if (!hoverPosition || hoverPosition.plane !== plane) return false
      const { startX, startY, itemWidth, itemHeight } = hoverPosition
      return (
        x >= startX &&
        x < startX + (itemWidth || 1) &&
        y >= startY &&
        y < startY + (itemHeight || 1)
      )
    },
    [hoverPosition]
  )

  const getItemsForPlane = useCallback(
    (plane) =>
      Object.values(droppedItems).filter(
        (item) => item.isCenter && item.individual_space === plane
      ),
    [droppedItems]
  )

  const placeGhostAtCenter = useCallback(
    (item, plane) => {
      const itemWidth = item.width || 1
      const itemHeight = item.height || 1
      const rows = plane === 'floor' ? gridSize : wallRows
      const startX = Math.max(0, Math.floor((gridSize - itemWidth) / 2))
      const startY = Math.max(0, Math.floor((rows - itemHeight) / 2))
      setGhostItem({
        ...item,
        plane,
        startX,
        startY,
        itemWidth,
        itemHeight,
        isGhost: true,
        reverse_flag: false,
        displayImage: item.left_material_image_url,
        originalPosition: null
      })
    },
    [gridSize, wallRows]
  )

  const placeGhostAt = useCallback((item, plane, x, y) => {
    const itemWidth = item.width || 1
    const itemHeight = item.height || 1
    setGhostItem({
      ...item,
      plane,
      startX: x,
      startY: y,
      itemWidth,
      itemHeight,
      isGhost: true,
      reverse_flag: false,
      displayImage: item.left_material_image_url,
      originalPosition: null
    })
  }, [])

  const convertItemToGhost = useCallback(
    (plane, itemData) => {
      const { startX, startY, itemWidth, itemHeight, positionId } = itemData

      const tilesSnapshot = {}
      for (let dx = 0; dx < itemWidth; dx++) {
        for (let dy = 0; dy < itemHeight; dy++) {
          const key = `${plane}-${startX + dx}-${startY + dy}`
          tilesSnapshot[key] = droppedItems[key]
        }
      }

      setDroppedItems((prev) => {
        const newItems = { ...prev }
        for (let dx = 0; dx < itemWidth; dx++) {
          for (let dy = 0; dy < itemHeight; dy++) {
            delete newItems[`${plane}-${startX + dx}-${startY + dy}`]
          }
        }
        return newItems
      })

      setGhostItem({
        ...itemData,
        plane,
        startX,
        startY,
        itemWidth,
        itemHeight,
        isGhost: true,
        originalPosition: {
          plane,
          startX,
          startY,
          itemWidth,
          itemHeight,
          positionId,
          tilesSnapshot
        }
      })
    },
    [droppedItems]
  )

  const moveGhost = useCallback((startX, startY, newPlane) => {
    setGhostItem((prev) => {
      if (!prev) return null
      return { ...prev, startX, startY, plane: newPlane ?? prev.plane }
    })
  }, [])

  const confirmGhost = useCallback(() => {
    if (!ghostItem) return
    const { plane, startX, startY, itemWidth, itemHeight, originalPosition } = ghostItem

    if (originalPosition?.positionId) {
      setItemsToDestroy((prev) => Array.from(new Set([...prev, originalPosition.positionId])))
    }

    const centerX = startX + Math.floor(itemWidth / 2)
    const centerY = startY + Math.floor(itemHeight / 2)
    const centerKey = `${plane}-${centerX}-${centerY}`

    setDroppedItems((prev) => {
      const existingInstances = Object.values(prev).filter(
        (d) => d.isCenter && d.id === ghostItem.id
      )
      const nextIndex =
        existingInstances.length > 0
          ? Math.max(...existingInstances.map((i) => i.itemIndex || 0)) + 1
          : 1

      const newItems = { ...prev }
      for (let dx = 0; dx < itemWidth; dx++) {
        for (let dy = 0; dy < itemHeight; dy++) {
          const key = `${plane}-${startX + dx}-${startY + dy}`
          if (startX + dx === centerX && startY + dy === centerY) {
            newItems[key] = {
              ...ghostItem,
              individual_space: plane,
              startX,
              startY,
              isCenter: true,
              itemWidth,
              itemHeight,
              itemIndex: originalPosition ? ghostItem.itemIndex || 1 : nextIndex,
              reverse_flag: ghostItem.reverse_flag || false,
              displayImage: ghostItem.displayImage,
              isDropped: true,
              isGhost: false,
              isSavedInGridArea: true,
              positionId: null
            }
          } else {
            newItems[key] = { occupied: true, startX, startY, itemWidth, itemHeight }
          }
        }
      }
      return newItems
    })

    setJustDroppedKey(centerKey)
    setTimeout(() => setJustDroppedKey(null), 400)
    setGhostItem(null)
  }, [ghostItem])

  const cancelGhost = useCallback(() => {
    setGhostItem(null)
  }, [])

  const rotateGhost = useCallback(() => {
    setGhostItem((prev) => {
      if (!prev) return null
      const newReverseFlag = !prev.reverse_flag
      const { width: newWidth, height: newHeight } = getEffectiveDimensions(prev, newReverseFlag)

      const maxY = prev.plane === 'floor' ? gridSize : wallRows
      if (prev.startX + newWidth > gridSize || prev.startY + newHeight > maxY) return prev

      const newDisplayImage =
        newReverseFlag && prev.right_material_image_url
          ? prev.right_material_image_url
          : prev.left_material_image_url

      return {
        ...prev,
        reverse_flag: newReverseFlag,
        displayImage: newDisplayImage,
        itemWidth: newWidth,
        itemHeight: newHeight
      }
    })
  }, [gridSize, wallRows])

  const handleCancel = useCallback(() => {
    setDroppedItems(savedSnapshot)
    setHoverPosition(null)
    setGhostItem(null)
  }, [savedSnapshot])

  const takeSnapshot = useCallback(() => {
    setSavedSnapshot({ ...droppedItems })
  }, [droppedItems])

  const getPositionsForSave = useCallback(() => {
    const positions = []

    Object.entries(droppedItems).forEach(([key, itemData]) => {
      if (itemData.isCenter) {
        const snapshotItem = savedSnapshot[key]
        const existsInSnapshot = snapshotItem && snapshotItem.isCenter && !!itemData.positionId
        const isDestroyedItem = itemData.positionId && itemsToDestroy.includes(itemData.positionId)
        const reverseFlagChanged =
          existsInSnapshot && snapshotItem.reverse_flag !== itemData.reverse_flag

        if (!existsInSnapshot || isDestroyedItem) {
          if (!itemData.isSavedInGridArea) {
            setItemAttachedAmounts((prev) => {
              const currentAttachedAmount = prev[itemData.id] ?? itemData.attached_amount ?? 0
              return { ...prev, [itemData.id]: currentAttachedAmount + 1 }
            })
          }

          const plane = itemData.individual_space
          const wallPosition = plane === 'left' || plane === 'right' ? plane : null

          positions.push({
            user_character_cloth_id: itemData.id,
            grid_x: itemData.startX,
            grid_y: itemData.startY,
            index: itemData.itemIndex || 1,
            reverse_flag: !!itemData.reverse_flag,
            ...(wallPosition && { wall_position: wallPosition })
          })
        } else if (reverseFlagChanged) {
          if (itemData.positionId) {
            positions.push({ id: itemData.positionId, _destroy: true })
          }

          const plane = itemData.individual_space
          const wallPosition = plane === 'left' || plane === 'right' ? plane : null

          positions.push({
            user_character_cloth_id: itemData.id,
            grid_x: itemData.startX,
            grid_y: itemData.startY,
            index: itemData.itemIndex || 1,
            reverse_flag: !!itemData.reverse_flag,
            ...(wallPosition && { wall_position: wallPosition })
          })
        }
      }
    })

    return positions
  }, [droppedItems, savedSnapshot, itemsToDestroy])

  const loadSavedPositions = useCallback((savedPositions, itemsData) => {
    if (!savedPositions || !Array.isArray(savedPositions) || savedPositions.length === 0) {
      setDroppedItems({})
      setSavedSnapshot({})
      return
    }

    const newDroppedItems = {}

    savedPositions.forEach((position) => {
      const item = itemsData.find((i) => i.id === position.user_character_cloth_id)
      const rawPlane = position.individual_space || 'floor'
      const wallPosition = position.wall_position
      const plane = rawPlane === 'wall' ? (wallPosition === 'right' ? 'right' : 'left') : rawPlane
      const startX = position.grid_x
      const startY = position.grid_y
      const reverseFlag = !!position.reverse_flag
      const dims = item ? getEffectiveDimensions(item, reverseFlag) : { width: 1, height: 1 }
      const itemWidth = dims.width
      const itemHeight = dims.height
      const itemIndex = position.index || 1
      const positionId = position.id
      const posIconUrl = position.icon_url
      const posLeftUrl = position.left_material_image_url || ''
      const posRightUrl = position.right_material_image_url || ''
      const centerX = startX + Math.floor(itemWidth / 2)
      const centerY = startY + Math.floor(itemHeight / 2)

      for (let dx = 0; dx < itemWidth; dx++) {
        for (let dy = 0; dy < itemHeight; dy++) {
          const key = `${plane}-${startX + dx}-${startY + dy}`
          if (startX + dx === centerX && startY + dy === centerY) {
            newDroppedItems[key] = {
              ...(item || {}),
              id: position.user_character_cloth_id,
              category: position.category,
              individual_space: plane,
              icon_url: posIconUrl,
              startX,
              startY,
              isCenter: true,
              itemWidth,
              itemHeight,
              rightWidth: item?.rightWidth || item?.width || 1,
              rightHeight: item?.rightHeight || item?.height || 1,
              itemIndex,
              reverse_flag: reverseFlag,
              positionId,
              displayImage: reverseFlag && posRightUrl ? posRightUrl : posLeftUrl,
              left_material_image_url: posLeftUrl,
              right_material_image_url: posRightUrl,
              isSavedInGridArea: true
            }
          } else {
            newDroppedItems[key] = {
              occupied: true,
              startX,
              startY,
              itemWidth,
              itemHeight,
              positionId
            }
          }
        }
      }
    })

    setDroppedItems(newDroppedItems)
    setSavedSnapshot(newDroppedItems)
  }, [])

  return {
    droppedItems,
    justDroppedKey,
    savedSnapshot,
    hoverPosition,
    draggingItem,
    itemsToDestroy,
    itemAttachedAmounts,
    setItemsToDestroy,
    setItemAttachedAmounts,
    setDroppedItems,
    canPlace,
    getItemAtPosition,
    isTileHovered,
    getItemsForPlane,
    setHoverPosition,
    setDraggingItem,
    handleCancel,
    getPositionsForSave,
    loadSavedPositions,
    takeSnapshot,
    ghostItem,
    placeGhostAtCenter,
    placeGhostAt,
    convertItemToGhost,
    moveGhost,
    confirmGhost,
    cancelGhost,
    rotateGhost
  }
}
